import React from 'react';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { resolveChatSessionCapabilities } from '../../../domain/resolvers/resolveChatSessionCapabilities';
import { resolveSessionAgent } from '../../../domain/resolvers/resolveSessionAgent';
import { setThinkLonger } from '../../../redux/features/chatUi/chatUiSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import useKnowledgeSources, { KnowledgeSourceAgentOption } from './useKnowledgeSources';
import useSuggestedPrompts, { SuggestedPromptCategoryOption } from './useSuggestedPrompts';
import { UserRole } from '../../../domain/users/models/User';

export interface UseToolkitTrayVm {
    canShowKnowledgeSources: boolean;
    canShowPersona: boolean;
    canShowThinkLonger: boolean;
    isThinkLongerEnabled: boolean;
    shouldExpandSuggestedPromptCategoriesByDefault: boolean;
    isSuggestedPromptsOpen: boolean;
    isDropUp: boolean;
    suggestedPromptCategories: SuggestedPromptCategoryOption[];
    isKnowledgeSourcesOpen: boolean;
    knowledgeSourceAgents: KnowledgeSourceAgentOption[];
    selectedKnowledgeSourceIds: string[];
    areAllKnowledgeSourcesSelected: boolean;
    onToggleSuggestedPromptsOpen: () => void;
    onCloseSuggestedPrompts: () => void;
    onToggleKnowledgeSourcesOpen: () => void;
    onToggleKnowledgeSource: (agentId: string) => void;
    onToggleAllKnowledgeSources: () => void;
    onCloseKnowledgeSources: () => void;
    onToggleThinkLonger: () => void;
}

export const useToolkitTray = (): UseToolkitTrayVm => {
    const dispatch = useAppDispatch();
    const session = useAppSelector(selectSelectedSession);
    const sessionId = useAppSelector((state) => state.chatUi.selectedSessionId);
    const agentsById = useAppSelector((state) => state.agents.byId);
    const isThinkLongerEnabled = useAppSelector((state) =>
        sessionId ? state.chatUi.bySessionId[sessionId]?.thinkLonger ?? false : false,
    );
    const activeUserInfo = useAppSelector((state) => state.app.activeUserInfo);

    const resolvedSessionAgent = React.useMemo(
        () => (session ? resolveSessionAgent(session, agentsById) : null),
        [agentsById, session],
    );

    const capabilities = React.useMemo(
        () => (session ? resolveChatSessionCapabilities(session, resolvedSessionAgent) : null),
        [resolvedSessionAgent, session],
    );

    // Only show Knowledge Sources if the session supports it and the user is not Restricted Access
    const canShowKnowledgeSources = capabilities?.supportsKnowledgeSources === true && activeUserInfo?.role !== UserRole.RestrictedAccess;
    const canShowPersona = capabilities?.supportsPersonaCustomization === true;
    const canShowThinkLonger = capabilities?.supportsThinkLonger === true;

    // call useSuggestedPrompts and useKnowledgeSources hooks here to coordinate the open/close state between the two trays. would also be valid to move those into the component. if we did it that way, we'd have to move the onToggles for suggested prompts and knowledge sources into the component (also fine)
    const suggestedPromptsVm = useSuggestedPrompts({
        agentsById,
    });

    const knowledgeSourcesVm = useKnowledgeSources({
        sessionId,
        agentsById,
        canShowKnowledgeSources,
    });

    const onToggleSuggestedPromptsOpen = React.useCallback((): void => {
        if (knowledgeSourcesVm.isKnowledgeSourcesOpen) {
            knowledgeSourcesVm.onCloseKnowledgeSources();
        }

        suggestedPromptsVm.onToggleSuggestedPromptsOpen();
    }, [
        knowledgeSourcesVm.isKnowledgeSourcesOpen,
        knowledgeSourcesVm.onCloseKnowledgeSources,
        suggestedPromptsVm.onToggleSuggestedPromptsOpen,
    ]);

    const onToggleKnowledgeSourcesOpen = React.useCallback((): void => {
        if (suggestedPromptsVm.isSuggestedPromptsOpen) {
            suggestedPromptsVm.onCloseSuggestedPrompts();
        }

        knowledgeSourcesVm.onToggleKnowledgeSourcesOpen();
    }, [
        knowledgeSourcesVm.onToggleKnowledgeSourcesOpen,
        suggestedPromptsVm.isSuggestedPromptsOpen,
        suggestedPromptsVm.onCloseSuggestedPrompts,
    ]);

    const onToggleThinkLonger = React.useCallback((): void => {
        if (!sessionId) {
            return;
        }

        dispatch(
            setThinkLonger({
                sessionId,
                value: !isThinkLongerEnabled,
            }),
        );
    }, [dispatch, isThinkLongerEnabled, sessionId]);

    return {
        canShowKnowledgeSources,
        canShowPersona,
        canShowThinkLonger,
        isThinkLongerEnabled,
        shouldExpandSuggestedPromptCategoriesByDefault:
            suggestedPromptsVm.shouldExpandSuggestedPromptCategoriesByDefault,
        isSuggestedPromptsOpen: suggestedPromptsVm.isSuggestedPromptsOpen,
        isDropUp: suggestedPromptsVm.isDropUp,
        suggestedPromptCategories: suggestedPromptsVm.suggestedPromptCategories,
        isKnowledgeSourcesOpen: knowledgeSourcesVm.isKnowledgeSourcesOpen,
        knowledgeSourceAgents: knowledgeSourcesVm.knowledgeSourceAgents,
        selectedKnowledgeSourceIds: knowledgeSourcesVm.selectedKnowledgeSourceIds,
        areAllKnowledgeSourcesSelected: knowledgeSourcesVm.areAllKnowledgeSourcesSelected,
        onToggleSuggestedPromptsOpen,
        onCloseSuggestedPrompts: suggestedPromptsVm.onCloseSuggestedPrompts,
        onToggleKnowledgeSourcesOpen,
        onToggleKnowledgeSource: knowledgeSourcesVm.onToggleKnowledgeSource,
        onToggleAllKnowledgeSources: knowledgeSourcesVm.onToggleAllKnowledgeSources,
        onCloseKnowledgeSources: knowledgeSourcesVm.onCloseKnowledgeSources,
        onToggleThinkLonger,
    };
};

export default useToolkitTray;